import React, { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import api from "../api/axios";
import PublicNav from "../components/PublicNav";

const C = {
  green:   "#006B3C",
  greenDk: "#004D2B",
  greenLt: "#E8F5EE",
  yellow:  "#FFC300",
  gray:    "#F5F5F5",
  grayBd:  "#E0E0E0",
  text:    "#1A1A1A",
  textSub: "#666666",
};

/**
 * Vérification des coordonnées après inscription (remarque n°5) :
 * code à 6 chiffres reçu par e-mail, code reçu par SMS/WhatsApp.
 * Un compte peut n'avoir QUE un email, QUE un téléphone, ou les deux -
 * on n'affiche et ne vérifie que les canaux réellement renseignés à
 * l'inscription.
 * Tant qu'aucun fournisseur SMS n'est configuré côté back (SMS_DSN),
 * la vérification téléphone peut échouer à l'envoi - on l'indique
 * clairement plutôt que de laisser l'utilisateur bloqué sans explication,
 * et on permet de continuer vers le tableau de bord malgré tout.
 */
const CodeBlock = ({ title, subtitle, verified, sending, verifying, sendError, verifyError, onResend, onConfirm }) => {
  const [code, setCode] = useState("");

  return (
    <div style={{
      border: `1.5px solid ${verified ? C.green : C.grayBd}`,
      background: verified ? C.greenLt : "#fff",
      borderRadius: 14, padding: 20, marginBottom: 16,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 14, color: C.text }}>{title}</div>
          <div style={{ fontSize: 12, color: C.textSub, marginTop: 2 }}>{subtitle}</div>
        </div>
        {verified && <span style={{ color: C.green, fontWeight: 700, fontSize: 13 }}>✓ Vérifié</span>}
      </div>

      {!verified && (
        <>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              value={code}
              onChange={e => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="Code à 6 chiffres"
              style={{ flex: 1, border: `1.5px solid ${C.grayBd}`, borderRadius: 10, padding: "10px 12px", fontSize: 15, letterSpacing: 3, textAlign: "center" }}
            />
            <button
              onClick={() => onConfirm(code)}
              disabled={code.length !== 6 || verifying}
              style={{ background: C.green, color: "#fff", border: "none", borderRadius: 10, padding: "0 18px", fontWeight: 700, cursor: "pointer", opacity: code.length === 6 ? 1 : 0.5 }}>
              {verifying ? "..." : "Valider"}
            </button>
          </div>
          {verifyError && <p style={{ color: "#ef4444", fontSize: 12, marginTop: 6 }}>{verifyError}</p>}
          {sendError && <p style={{ color: "#B45309", fontSize: 12, marginTop: 6 }}>{sendError}</p>}
          <button
            onClick={onResend}
            disabled={sending}
            style={{ background: "none", border: "none", color: C.green, fontSize: 12, fontWeight: 600, cursor: "pointer", marginTop: 8, padding: 0 }}>
            {sending ? "Envoi..." : "Renvoyer le code"}
          </button>
        </>
      )}
    </div>
  );
};

export default function VerifyAccount() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || "";
  const phone = location.state?.phone || "";
  const hasEmail = email.trim().length > 0;
  const hasPhone = phone.trim().length > 0;

  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [emailSending,  setEmailSending]  = useState(false);
  const [phoneSending,  setPhoneSending]  = useState(false);
  const [emailVerifying, setEmailVerifying] = useState(false);
  const [phoneVerifying, setPhoneVerifying] = useState(false);
  const [emailSendErr,  setEmailSendErr]  = useState("");
  const [phoneSendErr,  setPhoneSendErr]  = useState("");
  const [emailVerifyErr, setEmailVerifyErr] = useState("");
  const [phoneVerifyErr, setPhoneVerifyErr] = useState("");

  if (!hasEmail && !hasPhone) {
    // Page atteinte directement sans passer par l'inscription : on ne peut
    // pas savoir quel compte vérifier.
    return (
      <>
        <PublicNav />
        <div style={{ maxWidth: 480, margin: "80px auto", textAlign: "center", padding: 24 }}>
          <p style={{ color: C.textSub, marginBottom: 16 }}>Aucun compte à vérifier pour l'instant.</p>
          <Link to="/register" style={{ color: C.green, fontWeight: 700 }}>Retour à l'inscription</Link>
        </div>
      </>
    );
  }

  // On envoie email ET téléphone au backend à chaque appel : selon lequel
  // des deux est vide côté compte, le serveur retrouve l'utilisateur avec
  // celui qui est renseigné (voir la note d'implémentation backend).
  const identifier = { email: email || undefined, phone: phone || undefined };

  const resendEmail = async () => {
    setEmailSending(true); setEmailSendErr("");
    try {
      await api.post("/verify/email/resend", identifier);
    } catch {
      setEmailSendErr("Impossible d'envoyer le code pour le moment.");
    } finally {
      setEmailSending(false);
    }
  };

  const confirmEmail = async (code) => {
    setEmailVerifying(true); setEmailVerifyErr("");
    try {
      await api.post("/verify/email/confirm", { ...identifier, code });
      setEmailVerified(true);
    } catch (e) {
      setEmailVerifyErr(e.response?.data?.error || "Code invalide");
    } finally {
      setEmailVerifying(false);
    }
  };

  const resendPhone = async () => {
    setPhoneSending(true); setPhoneSendErr("");
    try {
      const r = await api.post("/verify/phone/resend", identifier);
      if (r.data?.sent === false) setPhoneSendErr(r.data.message);
    } catch {
      setPhoneSendErr("Impossible d'envoyer le code pour le moment.");
    } finally {
      setPhoneSending(false);
    }
  };

  const confirmPhone = async (code) => {
    setPhoneVerifying(true); setPhoneVerifyErr("");
    try {
      await api.post("/verify/phone/confirm", { ...identifier, code });
      setPhoneVerified(true);
    } catch (e) {
      setPhoneVerifyErr(e.response?.data?.error || "Code invalide");
    } finally {
      setPhoneVerifying(false);
    }
  };

  // On ne considère "tout vérifié" que pour les canaux réellement fournis.
  const allDone = (!hasEmail || emailVerified) && (!hasPhone || phoneVerified);

  return (
    <>
      <PublicNav />
      <div style={{ minHeight: "calc(100vh - 64px)", background: C.gray, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
      <div style={{ maxWidth: 460, width: "100%", background: "#fff", borderRadius: 18, padding: 32, boxShadow: "0 10px 40px rgba(0,0,0,0.08)" }}>
        <div style={{ textAlign: "center", marginBottom: 24 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>📬</div>
          <h1 style={{ fontSize: 20, fontWeight: 800, color: C.greenDk, margin: 0 }}>Vérifiez votre compte</h1>
          <p style={{ fontSize: 13, color: C.textSub, marginTop: 6 }}>
            {hasEmail && hasPhone && <>Des codes ont été envoyés à <strong>{email}</strong> et par SMS/WhatsApp au <strong>{phone}</strong>.</>}
            {hasEmail && !hasPhone && <>Un code a été envoyé à <strong>{email}</strong>.</>}
            {!hasEmail && hasPhone && <>Un code a été envoyé par SMS/WhatsApp au <strong>{phone}</strong>.</>}
          </p>
        </div>

        {hasEmail && (
          <CodeBlock
            title="E-mail"
            subtitle="Entrez le code reçu par e-mail"
            verified={emailVerified}
            sending={emailSending} verifying={emailVerifying}
            sendError={emailSendErr} verifyError={emailVerifyErr}
            onResend={resendEmail} onConfirm={confirmEmail}
          />
        )}
        {hasPhone && (
          <CodeBlock
            title="Téléphone (SMS / WhatsApp)"
            subtitle="Entrez le code reçu par SMS ou WhatsApp"
            verified={phoneVerified}
            sending={phoneSending} verifying={phoneVerifying}
            sendError={phoneSendErr} verifyError={phoneVerifyErr}
            onResend={resendPhone} onConfirm={confirmPhone}
          />
        )}

        <button
          onClick={() => navigate("/dashboard")}
          style={{
            width: "100%", marginTop: 8, padding: "13px 20px", borderRadius: 12,
            background: allDone ? C.green : "#fff",
            color: allDone ? "#fff" : C.textSub,
            border: `1.5px solid ${allDone ? C.green : C.grayBd}`,
            fontWeight: 700, fontSize: 14, cursor: "pointer",
          }}>
          {allDone ? "Continuer vers mon espace →" : "Continuer plus tard →"}
        </button>
      </div>
      </div>
    </>
  );
}
